import React from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { SUB_TASK_LEVEL } from '@atlassian/jira-portfolio-3-common';
import { EpicLevelSpotlightTarget } from '@atlassian/jira-portfolio-3-onboarding/src/ui/spotlight-target';
import {
	TABLE_BLANK_ROW,
	TABLE_GROUP,
	TABLE_GROUP_HEADER,
	TABLE_HEADER,
	TABLE_INLINE_CREATE,
	TABLE_ISSUE,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import {
	getRowHighlighter,
	getRowHighlighterLegacy,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/util';
import { getBody } from '@atlassian/jira-portfolio-3-portfolio/src/common/dom';
import { GROUPING } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import type { Warning } from '@atlassian/jira-portfolio-3-portfolio/src/common/warning-details/types';
import Group from '../group';
import InlineCreate from '../inline-create';
import { SortableIssuesWithoutParentHeader } from '../list';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from '../styles.module.css';
import BlankRow from './blank-row';
import type { TableRowProps } from './types';

const TableRow = ({
	SelectableIssueComponent,
	SortableIssueComponent,
	SortableIssuesWithoutParentComponent,
	issues,
	onToggle,
	isReadOnly,
	rankDigits,
	startLevel,
	warnings,
	showWarning,
	isGroupingByMultiSelectCustomField,
	grouping,
	key,
	index,
	style,
	isScrolling,
	drop,
}: TableRowProps) => {
	const item = issues[index];
	const { tag } = item;

	if (item.tag === TABLE_ISSUE) {
		const { value: issue, parentGroup, groupCombination } = item;
		const issueId = issue.id || '';
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		const issueWarnings: Warning[] = (warnings.byIssue[issueId] as any) || [];
		// remove when cleaning ff arj-remove-row-warning-highlighting_jqjs5
		const hasWarnings = issueWarnings.length > 0 && showWarning;

		const hasIndentation = !!parentGroup;
		const { parentId } = drop;

		const highlighter = ff('arj-remove-row-warning-highlighting_jqjs5')
			? getRowHighlighter(issue.group, issue.id)
			: getRowHighlighterLegacy(issue.group, issue.id, hasWarnings);
		return (
			<div
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.table-row.table-issue"
				key={`${item.tag}-${item.group || ''}-${issue.id || key}`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
			>
				{highlighter}
				<SelectableIssueComponent issueId={issue.id} group={issue.group}>
					{({
						isSelected,
						hasChildSelected,
						toggleSelected,
					}: {
						isSelected: boolean;
						hasChildSelected: boolean;
						toggleSelected: () => {};
					}) => (
						<SortableIssueComponent
							isDropParent={parentId === issue.id}
							disabled={isReadOnly}
							index={index}
							onToggle={onToggle}
							hideRank={(issue.level || SUB_TASK_LEVEL) < startLevel}
							issue={issue}
							isReadOnly={isReadOnly}
							isScrolling={isScrolling}
							rankDigits={rankDigits}
							isSelected={isSelected}
							hasChildSelected={hasChildSelected}
							toggleSelected={toggleSelected}
							warnings={warnings}
							hasIndentation={hasIndentation}
							groupCombination={groupCombination}
							isGroupingByMultiSelectCustomField={isGroupingByMultiSelectCustomField}
						/>
					)}
				</SelectableIssueComponent>
			</div>
		);
	}

	if (item.tag === TABLE_INLINE_CREATE) {
		return (
			<div
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.table-row.table-inline-create"
				key={`${item.tag}-${item.group || ''}-${item.value.id || key}`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
			>
				<InlineCreate
					rootIndex={item.value.rootIndex}
					isReadOnly={isReadOnly}
					fixVersions={item.value.fixVersions?.map((x) => Number(x))}
					components={item.value.components?.map((x) => Number(x))}
					getContainer={getBody}
				/>
			</div>
		);
	}

	// Remove with the cleanup of com.atlassian.rm.jpo.ease-of-use.remove-no-parent-header
	if (item.tag === TABLE_HEADER) {
		const hasIndentation = !!item.parentGroup;
		const styleName = hasIndentation ? styles['row-with-indentation'] : '';
		return (
			<div
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.table-row.table-header"
				key={`${item.tag}-${item.group || ''}-${item.value.id || key}`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styleName}
			>
				<SortableIssuesWithoutParentHeader
					index={index}
					disabled={isReadOnly}
					issuesCount={item.value.issueCount}
					isExpanded={item.value.isExpanded}
					id={item.value.id}
					onToggle={onToggle}
				/>
			</div>
		);
	}

	if (item.tag === TABLE_GROUP_HEADER) {
		const { id, isExpanded, level, rootIssuesCount } = item.value;
		const hasIndentation = !!item.parentGroup;
		const styleName = hasIndentation ? styles['row-with-indentation'] : '';

		return EpicLevelSpotlightTarget(
			<div
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.table-row.table-group-header"
				key={`${item.tag}-${item.group || ''}-${id || key}`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styleName}
			>
				<SortableIssuesWithoutParentComponent
					disabled={isReadOnly}
					index={index}
					id={id || `${level.title}`}
					isExpanded={isExpanded}
					level={level}
					onToggle={onToggle}
					rankDigits={rankDigits}
					rootIssuesCount={rootIssuesCount}
				/>
			</div>,
			grouping === GROUPING.NONE,
			level?.level,
		);
	}

	if (item.tag === TABLE_BLANK_ROW) {
		return (
			<div
				key={key}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
				data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues.table-row.table-blank-row"
			>
				<BlankRow index={index} disabled={isReadOnly} />
			</div>
		);
	}

	if (item.tag === TABLE_GROUP) {
		// @ts-expect-error - TS2322 - Type 'TableGroupItem' is not assignable to type 'TableItem'.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		return <Group item={item} style={style} key={key} index={index} />;
	}

	throw new Error(`Unexpected TABLE item variant: ${tag}`);
};

export default TableRow;
